import _ from 'lodash'
import { v4 as uuid } from 'uuid'

import { selectOrdersByIds } from '@/entities/orders'
import { Order } from '@/entities/orders/models/Order'
import { runsheetShiftsSelectors, runsheetsSelectors, StopTypeEnum } from '@/entities/schedules'
import { RunsheetStop } from '@/entities/schedules/models/runsheet/RunsheetStop'
import { selectRunsheetStopsByIds } from '@/entities/schedules/selectors/runsheetStopsSelectors'
import { selectBusinessModel } from '@/entities/user'
import { BusinessModelEnum } from '@/entities/user/models/BusinessModel'
import { GridTableDragType } from '@/shared/components/GridTable/models/GridTableDragType'
import { mapToObject } from '@/shared/lib/mapToObject'
import { timeHhMmToMins, timeMinsToHhMm } from '@/shared/lib/timeHelper'
import { RootState } from '@/shared/store'
import { faEarth } from '@fortawesome/pro-solid-svg-icons'
import { createAsyncThunk, EntityId } from '@reduxjs/toolkit'

import { OrderStatusTypeString } from '../model/OrderStatusType'
import { RunsheetShiftGridRow } from '../model/runsheet/RunsheetShiftGridRow'
import {
  defaultAddressDetailsRow, defaultCustomerDetailsRow, defaultDepotDetailsRow, defaultOrderDetailsRow, defaultRunningTotalRow, defaultStopDetailsRow,
  ShiftRunningTotalRow,
} from '../model/ShiftGridRow'
import { upsertOneRunsheetShiftTable } from '../slices/runsheetShiftTablesSlice'

type UpdateRunsheetShiftTablePayload = {
  shiftId: EntityId
}

/*
  This thunk updates the state of a single shift table and should be called if the shifts or stops information changes.
*/
export const updateRunsheetShiftTableState = createAsyncThunk<void, UpdateRunsheetShiftTablePayload, { state: RootState }>(
  'runsheetShiftTable/updateSingleShiftTable',
  async ({ shiftId }, { getState, dispatch }) => {
    const state = getState()
    const businessModel = selectBusinessModel(state)
    const runsheetShift = runsheetShiftsSelectors.selectById(state, shiftId)
    const runsheet = runsheetsSelectors.selectById(state, runsheetShift?.runsheetId)
    const runsheetName = runsheet?.name || ''
    const runsheetDateOfOperation = runsheet?.dateOfOperation || ''
    const stops = selectRunsheetStopsByIds(state, runsheetShift?.stopIds)
    const orders = selectOrdersByIds(state, runsheetShift?.orderIds)

    if (!runsheetShift || !stops || !orders) {
      throw new Error('Redux thunk error [runsheetShiftTables/updateSingleShiftTable]: runsheet shift or stops or orders not found')
    }

    const calculateRunningTotals = (aStops: RunsheetStop[], aOrders: Order[]): ShiftRunningTotalRow[] => {
      const runningTotals: ShiftRunningTotalRow[] = []

      let runningWeight = 0
      let runningVolume = 0
      let runningQuantity = 0
      let runningDistance = 0
      let runningWorkingTimeInMins = 0
      let prevRunningTotalTime = ''

      for (let i = 0; i < aStops.length; i++) {
        const stop = aStops[i]
        let timeAtStopInMins = 0
        let runningWorkingTime = prevRunningTotalTime

        if (stop.stopType === StopTypeEnum.Order) {
          const stopOrder = aOrders.find(ord => ord.id === stop.orderId)

          if (!stopOrder) continue

          runningDistance += Math.min(0, stop.transitDistance)

          if (stop.isPickup) {
            runningWeight += stopOrder.weight
            runningVolume += stopOrder.volume
            runningQuantity += stopOrder.quantity
          } else {
            runningWeight -= stopOrder.weight || 0
            runningVolume -= stopOrder.volume || 0
            runningQuantity -= stopOrder.quantity || 0
          }

          // Working time
          timeAtStopInMins = timeHhMmToMins(stop.timeAtStop) || 0
          runningWorkingTimeInMins += timeAtStopInMins
          runningWorkingTime = timeMinsToHhMm(runningWorkingTimeInMins) || ''
        }

        runningTotals.push({
          runningWeight,
          runningVolume,
          runningQuantity,
          runningDistance,
          runningWorkingTime,
        })
        prevRunningTotalTime = runningWorkingTime
      }

      return runningTotals
    }

    const consolidateDepotRows = (depotRows: RunsheetShiftGridRow[]): RunsheetShiftGridRow => {
      const numStopsToConsolidate = depotRows.length

      depotRows[0].isSelectable = false
      depotRows[0].isDraggable = false
      depotRows[0].hasActions = false
      depotRows[0].textHighlight = depotRows[0].displayStopType === 'Pickup'

      if (numStopsToConsolidate === 1) {
        return depotRows[0]
      }

      const stopDetails = mapToObject(depotRows[0], defaultStopDetailsRow)

      stopDetails.sequence = depotRows[depotRows.length - 1].sequence
      stopDetails.adtTime = depotRows[depotRows.length - 1].adtTime

      const orderDetails = _.cloneDeep(defaultOrderDetailsRow)

      orderDetails.orderNumber = `[${numStopsToConsolidate} Orders]`

      for (let i = 0; i < depotRows.length; i++) {
        orderDetails.weight += depotRows[i].weight
        orderDetails.volume += depotRows[i].volume
        orderDetails.orderQuantity += depotRows[i].orderQuantity
      }

      const addressDetails = mapToObject(depotRows[0], defaultAddressDetailsRow)
      const customerDetails = mapToObject(depotRows[0], defaultCustomerDetailsRow)
      const depotDetails = mapToObject(depotRows[0], defaultDepotDetailsRow)
      const runningTotal = defaultRunningTotalRow

      runningTotal.runningWorkingTime = depotRows[numStopsToConsolidate - 1].runningWorkingTime

      return {
        id: depotRows[depotRows.length - 1]?.id || uuid(), // We are using the last stop id as the id for the consolidated row
        shiftId: shiftId,
        runsheetId: runsheetShift?.runsheetId,
        uiColor: depotRows[0].uiColor,
        isDepot: true,
        ...stopDetails,
        ...orderDetails,
        ...addressDetails,
        ...customerDetails,
        ...depotDetails,
        ...runningTotal,
      }
    }

    // Pre-compute running totals
    const runningTotals: ShiftRunningTotalRow[] = calculateRunningTotals(stops, orders)

    // Compile shift rows
    const shRows: RunsheetShiftGridRow[] = []

    let depotRows: RunsheetShiftGridRow[] = []
    let displaySequence = 0

    for (let i = 0; i < stops.length; i++) {
      const stop = stops[i]
      const prevStop = i === 0 ? stop : stops[i - 1]
      let runningTotal = runningTotals[i]

      let stopDetails = _.clone(defaultStopDetailsRow)
      let orderDetails = _.clone(defaultOrderDetailsRow)
      let addressDetails = _.clone(defaultAddressDetailsRow)
      let customerDetails = _.clone(defaultCustomerDetailsRow)
      let depotDetails = _.clone(defaultDepotDetailsRow)

      stopDetails.stopType = stop.stopType

      if (stop.stopType === StopTypeEnum.Start) {
        stopDetails.displayStopType = 'Start'
        stopDetails.plannedTime = stop.plannedTime
        stopDetails.etaTime = `${stop.plannedTime} ${stop.estimatedTime ? `[${stop.estimatedTime}]` : ''}`
        stopDetails.timeAtStop = stop.timeAtStop
        stopDetails.transitTime = stop.transitTime
        stopDetails.transitDistance = stop.transitDistance
        stopDetails.sequence = displaySequence
        stopDetails.customStatus = 'primary'
        stopDetails.isDroppable = true
      } else if (stop.stopType === StopTypeEnum.Finish) {
        stopDetails.displayStopType = 'Finish'
        stopDetails.plannedTime = stop.plannedTime
        stopDetails.etaTime = `${stop.plannedTime} ${stop.estimatedTime ? `[${stop.estimatedTime}]` : ''}`
        stopDetails.timeAtStop = stop.timeAtStop
        stopDetails.transitTime = stop.transitTime
        stopDetails.transitDistance = stop.transitDistance
        stopDetails.sequence = displaySequence
        stopDetails.customStatus = 'primary'
      } else if (stop.stopType === StopTypeEnum.LoadIndicator) {
        stopDetails = {
          ...stopDetails,
          displayStopType: `Load ${stop.loadNumber}`,
          customStatus: 'success',
        }
        orderDetails = {
          ...orderDetails,
          orderNumber: `${stop.orderCount} Order${stop.orderCount > 1 && 's'}`,
          weight: stop.totalWeight,
          volume: stop.totalVolume,
          orderQuantity: stop.totalQuantity,
        }
        runningTotal = {
          ...defaultRunningTotalRow,
          runningWorkingTime: stop.totalWorkingTime,
          runningDistance: stop.totalDistance,
        }
      } else if (stop.stopType === StopTypeEnum.Break) {
        stopDetails = {
          ...stopDetails,
          displayStopType: 'Break',
          sequence: displaySequence,
          plannedTime: stop.plannedTime,
          etaTime: `${stop.plannedTime} ${stop.estimatedTime ? `[${stop.estimatedTime}]` : ''}`,
          timeAtStop: stop.timeAtStop,
          transitTime: stop.transitTime,
          isDraggable: true,
          dragType: GridTableDragType.SHIFT_STOP_ROW,
          acceptDropTypes: [GridTableDragType.SHIFT_STOP_ROW, GridTableDragType.UNASSIGNED_ORDER_ROW, GridTableDragType.INBOX_ORDER_ROW],
          isDroppable: true,
          isSelectable: true,
          hasActions: true,
          hasHotAction: false,
        }
      } else if (stop.stopType === StopTypeEnum.Order) {
        const stopOrder = orders.find(ord => ord.id === stop.orderId)

        if (!stopOrder) continue
        stopDetails = {
          isJob: stop.isJob,
          isPickup: stop.isPickup,
          sequence: displaySequence,
          stopType: stop.stopType,
          displayStopType: stop.isPickup ? 'Pickup' : 'Delivery',
          plannedTime: stop.plannedTime,
          etaTime: `${stop.plannedTime} ${stop.estimatedTime ? `[${stop.estimatedTime}]` : ''}`,
          adtTime: stop.actualTime,
          timeAtStop: stop.timeAtStop,
          transitDistance: stop.transitDistance,
          transitTime: stop.transitTime,
          dragType: GridTableDragType.SHIFT_STOP_ROW,
          acceptDropTypes: [GridTableDragType.SHIFT_STOP_ROW, GridTableDragType.UNASSIGNED_ORDER_ROW, GridTableDragType.INBOX_ORDER_ROW],
          isDraggable: true,
          isDroppable: true,
          isSelectable: true,
          hasActions: stop.isJob ? true : false,
          hasHotAction: stop.isJob ? true : false,
          textHighlight: stop.isPickup,
        }

        orderDetails = {
          ...orderDetails,
          orderId: stop.orderId,
          orderNumber: stopOrder.orderNumber,
          preferredDateOfOperation: stopOrder.preferredDateOfOperation,
          weight: stopOrder.weight,
          volume: stopOrder.volume,
          orderQuantity: stopOrder?.orderItems?.length || 0,
          timeWindow: `${stopOrder.timeWindows[0]} - ${stopOrder.timeWindows[1]}`,
          serviceTime: 0,
          priority: stopOrder.priority,
          maxOnboardTime: stopOrder.maxOnboardTime,
          tripNumber: stopOrder.tripNumber,
          consignmentNumber: stopOrder.consignmentNumber || '',
          description: stopOrder.description || '',
          deliveryInstruction: stopOrder.deliveryInstruction || '',
          comment: stopOrder.comment || '',
          statusType: OrderStatusTypeString[stopOrder.statusType],
          statusCellStyle: {
            backgroundColor: stopOrder.bgColor,
            color: stopOrder.fgColor,
          },
        }

        const depotLocation = (stop.isJob == stop.isPickup) ? stopOrder.toLocation : stopOrder.fromLocation

        depotDetails = {
          ...depotDetails,
          depotCode: depotLocation.externalId || '',
          depotName: depotLocation.name,
          depotDisplayAddress: depotLocation.displayAddress,
          depotLatitude: depotLocation.y,
          depotLongitude: depotLocation.x,
        }

        const customerLocation = stop.isPickup ? stopOrder.fromLocation : stopOrder.toLocation

        addressDetails = {
          ...addressDetails,
          isGeocoded: customerLocation.isGeocoded,
          geocodeError: customerLocation?.isGeocoded ? undefined : {
            tooltipText: 'Order not geocoded',
            type: 'warning',
            icon: faEarth,
          },
          customerAddress: customerLocation.displayAddress,
          customerAdditional: customerLocation.additional,
          customerMapRef: customerLocation.mapRef,
          customerPostcode: customerLocation.postcode,
          customerState: customerLocation.state,
          customerSuburb: customerLocation.suburb,
          customerDisplayAddress: customerLocation.displayAddress,
          customerLatitude: customerLocation.y,
          customerLongitude: customerLocation.x,
        }

        customerDetails = {
          ...customerDetails,
          customerName: customerLocation.name,
          customerNumber: customerLocation.externalId || '',
        }
      }

      // Check if we need to consolidate the depot rows. To be eligible for being a depot row, we need:
      // (1). Business Model is Depot
      // (2). Previous stop is either Start or Load Indicator while current stop is an Order
      // (3). The current stop is a depot and has the same location as the previous depot row
      // (4). It is the first depot row after a consolidation
      const isDepotBusinessModel = businessModel == BusinessModelEnum.Depot
      const isSameDepotAsPrevStop = (stop.stopType === StopTypeEnum.Order &&
        prevStop.stopType === StopTypeEnum.Order &&
        !stop.isJob && !prevStop.isJob &&
        stop.isPickup === prevStop.isPickup &&
        stop.addressId === prevStop.addressId)

      if (isDepotBusinessModel && isSameDepotAsPrevStop) {
        depotRows.push({
          ...stopDetails,
          ...orderDetails,
          ...addressDetails,
          ...customerDetails,
          ...depotDetails,
          ...runningTotal,
          id: stop.id,
          shiftId: shiftId,
          runsheetId: runsheetShift?.runsheetId,
          uiColor: runsheetShift.uiColor,
        })
      } else {
        if (depotRows && depotRows.length > 0) {
          // Consolidate the rows into one row
          const consolidatedRow = consolidateDepotRows(depotRows)

          shRows.push(consolidatedRow)
          displaySequence += 1
          depotRows = []
        }

        // Check condition (4) above where the current stop is the first depot row after a consolidation
        if (stop.stopType === StopTypeEnum.Order && !stop.isJob) {
          depotRows.push({
            ...stopDetails,
            ...orderDetails,
            ...addressDetails,
            ...customerDetails,
            ...depotDetails,
            ...runningTotal,
            uiColor: runsheetShift.uiColor,
            shiftId: shiftId,
            runsheetId: runsheetShift?.runsheetId,
            id: stop.id,
          })
        } else {
          shRows.push({
            ...stopDetails,
            ...orderDetails,
            ...addressDetails,
            ...customerDetails,
            ...depotDetails,
            ...runningTotal,
            id: stop.id,
            shiftId: shiftId,
            runsheetId: runsheetShift?.runsheetId,
            uiColor: runsheetShift.uiColor,
            sequence: displaySequence,
          })

          // We don't increment the display sequence for Load Indicators and Start stops
          if (stop.stopType !== StopTypeEnum.LoadIndicator) {
            displaySequence += 1
          }
        }
      }
    }

    dispatch(upsertOneRunsheetShiftTable({
      shiftId,
      runsheetId: runsheetShift?.runsheetId,
      isLoading: false,
      scheduleName: runsheetName,
      runsheetDateOfOperation,
      shiftName: runsheetShift?.name || '',
      shiftUiColor: runsheetShift?.uiColor || '',
      rows: shRows,
    }))
  },
)
